import React from "react";
import {Tag, TagCategory} from "../../gen/api";
import TagTable from "../Tags/TagTable";
import {selectFilters} from "../../store/selectors";
import {useSelector} from "react-redux";

type Props = {
  tags: TagCategory[]
  onFiltersChange: (filters: string[]) => void
}

/**
 * This component is responsible for displaying the search table.
 */
export default function SearchTable({tags, onFiltersChange}: Props) {
  const selectedFilters = useSelector(selectFilters);

  const handleOnClick = (tagId: string) => {
    const clickedTag = tags
      .flatMap(cluster => cluster.tags)
      .flatMap(group => group.childTags ? [group, ...group.childTags] : [group])
      .find(tag => tag!.id === tagId);

    if (!clickedTag) return;
    let childTagIds = clickedTag.childTags?.map(tag => tag.id!);
    childTagIds = childTagIds !== undefined ? childTagIds : [];
    const parentTag = tags
      .flatMap(cluster => cluster.tags)
      .find(group => group.childTags?.map(tag => tag.id).includes(clickedTag.id))

    let newSelectedFilters: string[];

    // If the clicked tag is already selected, remove it and all child tags from the array
    if (selectedFilters.includes(clickedTag.id!)) {
      // remove the clicked tag and all its children from the array
      newSelectedFilters = [...selectedFilters]
        .filter(tagId => tagId !== clickedTag.id && !childTagIds?.includes(tagId));

      // remove parent tag if one child tag is removed
      if (parentTag) {
        newSelectedFilters = newSelectedFilters.filter(e => e !== parentTag.id);
      }
    } else {
      // Tag has been selected
      newSelectedFilters = [...selectedFilters, clickedTag.id!, ...childTagIds];
      // If all children are selected, also select parent tag
      if (parentTag && parentTag.childTags?.every(tag => newSelectedFilters.includes(tag.id!))) {
        newSelectedFilters = [...newSelectedFilters, parentTag.id!]
      }
    }

    onFiltersChange(newSelectedFilters);
  }

  const handleToggleSubtree = (tag: Tag, checked: boolean) => {
    const collectOds = (t: Tag): string[] => [
      t.id!,
      ...(t.childTags?.flatMap(collectOds) ?? []),
    ];
    const subtreeIds = collectOds(tag);
    const newSelectedFilters = checked
      ? Array.from(new Set([...selectedFilters, ...subtreeIds]))
      : selectedFilters.filter(id => !subtreeIds.includes(id));

    onFiltersChange(newSelectedFilters);
  }

  return (
    <TagTable tagCategories={tags} handleOnClick={handleOnClick} handleToggleSubtree={handleToggleSubtree} selectedFilters={selectedFilters} />
  );
}